/**
 * restoreRuntimeSnapshot.js
 *
 * Reads, validates, and returns the persisted runtime snapshot.
 * Returns null if no snapshot exists, schema is invalid, or TTL has expired.
 *
 * Callers (KeviaContext) are responsible for merging the snapshot
 * into the runtime store and emitting RT_EVENTS.RUNTIME_RESTORED.
 */

import { SNAPSHOT_KEY }                        from "./persistenceSchema";
import { isValidSnapshot, isExpired, safeParseJSON } from "./persistenceGuards";
import { recoverSnapshot }                     from "../kevia-runtime/runtimeRecovery";

/**
 * @returns {object|null} — valid snapshot, or null on any failure/expiry
 */
export function restoreRuntimeSnapshot() {
  try {
    const raw = sessionStorage.getItem(SNAPSHOT_KEY);
    if (!raw) return null;

    const data = safeParseJSON(raw);
    if (!isValidSnapshot(data)) {
      sessionStorage.removeItem(SNAPSHOT_KEY);
      return null;
    }

    if (isExpired(data)) {
      sessionStorage.removeItem(SNAPSHOT_KEY);
      return null;
    }

    // FASE 23.8 — sanitize any corrupt/missing fields before returning
    return recoverSnapshot(data);
  } catch {
    return null;
  }
}

/** Explicitly clears the persisted snapshot (e.g. on sign-out). */
export function clearRuntimeSnapshot() {
  try { sessionStorage.removeItem(SNAPSHOT_KEY); } catch {}
}
